/**
 * The pieces of the owner's card rating (#55) that more than one page draws: the star
 * picker, the tag picker, and the read-only stars and tag chips.
 *
 * Owner-only, every one of them. A caller should only reach for these with a card whose
 * `ownerRating` the server actually sent, which it does for the owner alone.
 */
import { GOOD_TAGS, NOT_SO_GOOD_TAGS, ratingTag } from "./card-ratings";
import type { RatingTag } from "./card-ratings";

/** The small "Only you" marker on anything a visitor would never see. */
export function OnlyYou() {
  return (
    <span className="only-you">
      <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
        <rect x="2" y="5" width="8" height="6" rx="1" fill="currentColor" />
        <path d="M4 5V3.5a2 2 0 0 1 4 0V5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      </svg>
      Only you
    </span>
  );
}

/**
 * "0 · low effort" plus three stars, one tap each. 0 is its own button rather than "no
 * stars", because no stars is what an unrated card looks like and the two must not be
 * mistaken for each other.
 */
export function StarPicker({ value, onPick, compact = false, disabled = false }: {
  value: number | null;
  onPick: (rating: number) => void;
  compact?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className={`star-picker${compact ? " is-compact" : ""}`} role="group" aria-label="Rating">
      <button type="button" className="zero-button" aria-pressed={value === 0} onClick={() => onPick(0)} disabled={disabled}>
        0 · low effort
      </button>
      {[1, 2, 3].map((n) => (
        <button
          key={n}
          type="button"
          className={`star-button${value !== null && value >= n ? " is-on" : ""}`}
          aria-pressed={value === n}
          aria-label={`${n} of 3 stars`}
          onClick={() => onPick(n)}
          disabled={disabled}
        >★</button>
      ))}
    </div>
  );
}

function TagButton({ tag, selected, onToggle }: { tag: RatingTag; selected: boolean; onToggle: (key: string) => void }) {
  return (
    <button
      type="button"
      className={`rating-tag ${tag.good ? "is-good" : "is-not-good"}`}
      aria-pressed={selected}
      onClick={() => onToggle(tag.key)}
    >
      {tag.label}{tag.note && <small> ({tag.note})</small>}
    </button>
  );
}

/** Both groups of tags. Grey until picked; picked good ones go pale green, the rest pale red. */
export function TagPicker({ selected, onToggle }: { selected: readonly string[]; onToggle: (key: string) => void }) {
  return (
    <div className="rating-tag-groups">
      <div className="rating-tag-group">
        <span>Good things</span>
        <div className="rating-tag-list">
          {GOOD_TAGS.map((tag) => <TagButton key={tag.key} tag={tag} selected={selected.includes(tag.key)} onToggle={onToggle} />)}
        </div>
      </div>
      <div className="rating-tag-group">
        <span>Not so good</span>
        <div className="rating-tag-list">
          {NOT_SO_GOOD_TAGS.map((tag) => <TagButton key={tag.key} tag={tag} selected={selected.includes(tag.key)} onToggle={onToggle} />)}
        </div>
      </div>
    </div>
  );
}

/** A rating, read-only. Draws nothing for an unrated card unless asked to say so. */
export function RatingStars({ rating, sayUnrated = false }: { rating: number | null; sayUnrated?: boolean }) {
  if (rating === null) return sayUnrated ? <span className="rating-unrated">Unrated</span> : null;
  if (rating === 0) return <span className="rating-zero">0 · low effort</span>;
  return (
    <span className="rating-stars" role="img" aria-label={`${rating} of 3 stars`}>
      {"★".repeat(rating)}<span className="rating-stars-off">{"★".repeat(3 - rating)}</span>
    </span>
  );
}

/** Tag chips, with a count when a tag appears more than once. */
export function TagChips({ tags }: { tags: readonly (string | [string, number])[] }) {
  return (
    <div className="rating-chips">
      {tags.map((entry) => {
        const [key, count] = typeof entry === "string" ? [entry, 1] : entry;
        const tag = ratingTag(key);
        if (!tag) return null;
        return (
          <span key={key} className={`rating-chip ${tag.good ? "is-good" : "is-not-good"}`}>
            {tag.label}{count > 1 && ` ×${count}`}
          </span>
        );
      })}
    </div>
  );
}
